/**
 * Per-page draft buffer for the Providers tables.
 *
 * The Providers pages used to PATCH the controller on every keystroke
 * (toggle the enabled switch -> immediate PUT, type into the regions
 * cell -> immediate PUT). Operators asked for a single explicit
 * "save" affordance so they can stage a batch of edits across many
 * rows and commit (or roll back) them in one shot — matching the
 * Workload / Spec / Autoscaling pages whose Section header carries a
 * primary save button.
 *
 * This hook is the in-memory buffer that powers that flow. It owns:
 *
 *   - a per-`ProviderKind` overlay of pending edits (`enabled`,
 *     `inherit`, and a partial `values` map merged on top of the
 *     server's `values`);
 *   - convenience setters (`setEnabled`, `setVariant`)
 *     that mirror the previous immediate-PATCH callbacks but write to
 *     local state instead;
 *   - `save()` which serialises every dirty row into a
 *     `ProviderEntryPatch` and forwards it to the page-supplied
 *     `commit(kind, patch)` callback (a thin adapter over
 *     `useUpdateGlobalEntry` / `useUpdateServiceEntry` /
 *     `useUpdatePoolOverride`); and
 *   - `discard()` to drop every pending edit.
 *
 * Secret credentials still flow through the modal's own save path
 * (`ProviderConfigForm`) — those values are never on the row table
 * to begin with, so there's nothing for this buffer to hold for them.
 */
import { useCallback, useMemo, useState } from "react";

import {
  variantPatch,
  type ProviderEntryPatch,
  type ProviderKind,
} from "@/api/local/providers";

export interface ProviderDraft {
  enabled?: boolean;
  /** Overlay merged on top of the server-side `values` map. */
  values?: Record<string, string>;
}

export type ProviderDraftMap = Partial<Record<ProviderKind, ProviderDraft>>;

export interface UseProvidersDraftResult {
  drafts: ProviderDraftMap;
  dirtyKinds: ProviderKind[];
  isDirty: boolean;
  saving: boolean;
  error: string | null;
  /** Stage an enabled-toggle edit. */
  setEnabled: (kind: ProviderKind, enabled: boolean) => void;
  /** Stage a per-variant edit (regions list + variant_enabled flag). */
  setVariant: (
    kind: ProviderKind,
    variantKey: string,
    next: { regions: string[]; enabled: boolean },
  ) => void;
  save: () => Promise<void>;
  discard: () => void;
}

/**
 * `commit` is the page-specific adapter that knows how to turn
 * `(kind, patch)` into the right scoped PUT (global / service /
 * pool). It must throw on failure so the hook can surface the error.
 */
export function useProvidersDraft(
  commit: (kind: ProviderKind, patch: ProviderEntryPatch) => Promise<void>,
): UseProvidersDraftResult {
  const [drafts, setDrafts] = useState<ProviderDraftMap>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(
    (kind: ProviderKind, partial: ProviderDraft) => {
      setDrafts((prev) => {
        const cur = prev[kind] ?? {};
        const merged: ProviderDraft = { ...cur, ...partial };
        if (partial.values !== undefined) {
          // Successive variant edits on the same row must accumulate
          // (operator tweaks runpod.community regions, then
          // runpod.secure regions — both have to ride the next
          // save). Replacing the whole map would drop the earlier
          // edit on the floor.
          merged.values = { ...(cur.values ?? {}), ...partial.values };
        }
        return { ...prev, [kind]: merged };
      });
    },
    [],
  );

  const setEnabled = useCallback(
    (kind: ProviderKind, enabled: boolean) => update(kind, { enabled }),
    [update],
  );

  const setVariant = useCallback(
    (
      kind: ProviderKind,
      variantKey: string,
      next: { regions: string[]; enabled: boolean },
    ) =>
      update(kind, {
        values: variantPatch(kind, variantKey, next.regions, next.enabled),
      }),
    [update],
  );

  const dirtyKinds = useMemo(
    () =>
      (Object.keys(drafts) as ProviderKind[]).filter((k) => {
        const d = drafts[k];
        if (!d) return false;
        const hasField =
          d.enabled !== undefined ||
          (d.values !== undefined && Object.keys(d.values).length > 0);
        return hasField;
      }),
    [drafts],
  );

  const isDirty = dirtyKinds.length > 0;

  const discard = useCallback(() => {
    setDrafts({});
    setError(null);
  }, []);

  const save = useCallback(async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      for (const kind of dirtyKinds) {
        const d = drafts[kind]!;
        const patch: ProviderEntryPatch = {};
        if (d.enabled !== undefined) patch.enabled = d.enabled;
        if (d.values !== undefined) patch.values = d.values;
        await commit(kind, patch);
      }
      setDrafts({});
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }, [commit, dirtyKinds, drafts, isDirty, saving]);

  return {
    drafts,
    dirtyKinds,
    isDirty,
    saving,
    error,
    setEnabled,
    setVariant,
    save,
    discard,
  };
}

/**
 * Compose a server-side `ProviderEntry`-shaped record with the
 * staged draft so the row renders the *intended* state while edits
 * are pending. Pass `undefined` for `entry` when the server has no
 * row yet (the page-level "loading" gate handles that case).
 */
export function overlayValues(
  entry: { values?: Record<string, string> } | undefined,
  draft: ProviderDraft | undefined,
): Record<string, string> {
  return { ...(entry?.values ?? {}), ...(draft?.values ?? {}) };
}
